import { useMemo, useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, Home } from 'lucide-react';
import type { DocLibraryFolderRow } from '@/api/client';

/**
 * Document Library — genuinely recursive folder tree, unlike
 * `pages/binder/components/BinderFolderTree.tsx` (which hardcodes exactly
 * 2 grouping levels + a leaf list for the eISF zone/section/artifact
 * taxonomy). Library folders are user-created and nest to arbitrary
 * depth, so this groups the flat list into a `Map<parentId|null,
 * DocLibraryFolderRow[]>` once and recurses.
 */
export function DocLibraryFolderTree({
  folders,
  selectedFolderId,
  onSelect,
}: {
  folders: DocLibraryFolderRow[];
  /** Null = root (no folder selected — "All files"). */
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
}) {
  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, DocLibraryFolderRow[]>();
    for (const f of folders) {
      const key = f.parent_folder_id || null;
      const list = map.get(key) ?? [];
      list.push(f);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    }
    return map;
  }, [folders]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Auto-expand every ancestor of the currently-selected folder.
  useEffect(() => {
    if (!selectedFolderId) return;
    const byId = new Map(folders.map((f) => [f.folder_id, f]));
    const ancestors = new Set<string>();
    let cur = byId.get(selectedFolderId)?.parent_folder_id ?? null;
    while (cur) {
      ancestors.add(cur);
      cur = byId.get(cur)?.parent_folder_id ?? null;
    }
    if (ancestors.size > 0) {
      setExpanded((prev) => new Set([...prev, ...ancestors]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId, folders]);

  function toggle(folderId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  const topLevel = childrenByParent.get(null) ?? [];

  return (
    <nav className="text-sm">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left ${
          selectedFolderId === null ? 'bg-navy-50 font-medium text-navy' : 'text-ink-primary hover:bg-surface-muted'
        }`}
      >
        <Home className="h-3.5 w-3.5 shrink-0" />
        Home
      </button>
      <ul className="mt-0.5">
        {topLevel.map((folder) => (
          <FolderNode
            key={folder.folder_id}
            folder={folder}
            depth={0}
            childrenByParent={childrenByParent}
            expanded={expanded}
            onToggle={toggle}
            onSelect={onSelect}
            selectedFolderId={selectedFolderId}
          />
        ))}
      </ul>
    </nav>
  );
}

function FolderNode({
  folder,
  depth,
  childrenByParent,
  expanded,
  onToggle,
  onSelect,
  selectedFolderId,
}: {
  folder: DocLibraryFolderRow;
  depth: number;
  childrenByParent: Map<string | null, DocLibraryFolderRow[]>;
  expanded: Set<string>;
  onToggle: (folderId: string) => void;
  onSelect: (folderId: string | null) => void;
  selectedFolderId: string | null;
}) {
  const children = childrenByParent.get(folder.folder_id) ?? [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(folder.folder_id);
  const isSelected = selectedFolderId === folder.folder_id;

  return (
    <li>
      <div
        className={`flex items-center gap-1 rounded px-1 py-1.5 ${
          isSelected ? 'bg-navy-50 font-medium text-navy' : 'text-ink-primary hover:bg-surface-muted'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        <button
          type="button"
          onClick={() => hasChildren && onToggle(folder.folder_id)}
          className={`shrink-0 ${hasChildren ? 'text-ink-muted hover:text-ink-primary' : 'opacity-0'}`}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          tabIndex={hasChildren ? 0 : -1}
        >
          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => onSelect(folder.folder_id)}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          {isOpen ? <FolderOpen className="h-3.5 w-3.5 shrink-0" /> : <Folder className="h-3.5 w-3.5 shrink-0" />}
          <span className="truncate">{folder.name}</span>
        </button>
      </div>
      {hasChildren && isOpen && (
        <ul>
          {children.map((child) => (
            <FolderNode
              key={child.folder_id}
              folder={child}
              depth={depth + 1}
              childrenByParent={childrenByParent}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              selectedFolderId={selectedFolderId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
